import type { ConnectionState } from '@rocicorp/zero'
import type { Query } from '@tanstack/react-query'

import { isTRPCClientError } from '#lib/trpc/utils.ts'

/** Observe failed requests still used by the current screen, not old cache entries. */
export function isFailedConnectionQuery(query: Query): boolean {
  const error = query.state.error
  return (
    query.isActive() &&
    query.state.status === 'error' &&
    isTRPCClientError(error) &&
    error.data === undefined
  )
}

type WorkspaceConnectionStatus = {
  label: string
  description: string
  reconnecting?: boolean
  action: 'sign-in' | 'reload' | 'sync' | 'request' | undefined
}

export function getWorkspaceConnectionStatus(
  online: boolean,
  sync: ConnectionState,
  requestFailed: boolean,
): WorkspaceConnectionStatus | undefined {
  if (!online) {
    return {
      label: 'Offline',
      description: 'Check your internet connection. Reconnection is automatic.',
      action: undefined,
    }
  }
  switch (sync.name) {
    case 'needs-auth':
      return {
        label: 'Session expired',
        description: 'Sign in again to resume syncing.',
        action: 'sign-in',
      }
    case 'closed':
      return {
        label: 'Sync stopped',
        description: 'Reload Trevize to resume syncing.',
        action: 'reload',
      }
    case 'error':
      return {
        label: 'Sync unavailable',
        description:
          'Cached data is available. Try reconnecting to resume syncing.',
        action: 'sync',
      }
    case 'disconnected':
      return {
        label: 'Reconnecting',
        reconnecting: true,
        description:
          'Cached data is available. Sync will resume automatically.',
        action: undefined,
      }
  }
  if (requestFailed) {
    return {
      label: 'Connection error',
      description: 'A request could not reach the server. Try again.',
      action: 'request',
    }
  }
  return undefined
}
